// Wire shapes — kept in sync with llm-monitoring-worker/src/dashboard/types.ts.
// We don't share a package across workers/SPAs (project convention; see CLAUDE.md).

export interface OverviewKpis {
  activeUsers: number;
  sessions: number;
  totalCostUsd: number;
  completionRate: number;
  costPerCompletedSessionUsd: number;
  completedSessionCount: number;
}

export interface OverviewResponse {
  from: string;
  to: string;
  current: OverviewKpis;
  previousWindow: OverviewKpis;
  deltas: Record<keyof OverviewKpis, number | null>;
}

export type Granularity = "hour" | "day" | "month";
export const GRANULARITIES = ["hour", "day", "month"] as const;

export interface AdoptionPoint {
  /** Bucket label whose format depends on the response's granularity. */
  date: string;
  activeUsers: number;
}

export interface AdoptionSeriesResponse {
  granularity: Granularity;
  from: string;
  to: string;
  points: AdoptionPoint[];
}

export interface TokenSeriesPoint {
  date: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  totalTokens: number;
  costUsd: number;
}

export interface TokenSeriesResponse {
  granularity: Granularity;
  from: string;
  to: string;
  points: TokenSeriesPoint[];
}

export interface MonthlyKpis {
  totalCostUsd: number;
  activeUsers: number;
  sessions: number;
  completionRate: number;
  costPerActiveUserUsd: number;
  costPerCompletedSessionUsd: number;
  completedSessionCount: number;
}

export interface MonthlyOriginCost {
  adobe: number;
  nonAdobe: number;
}

export interface MonthlyPoint {
  month: string;
  totalCostUsd: number;
  costByModel: Record<string, number>;
  costByOrigin: MonthlyOriginCost;
  activeUsers: number;
  sessions: number;
  completedSessionCount: number;
  completionRate: number;
}

export interface MonthlySeriesResponse {
  from: string;
  to: string;
  points: MonthlyPoint[];
  current: MonthlyKpis;
  previousMonth: MonthlyKpis;
  deltas: Record<keyof MonthlyKpis, number | null>;
}

export const OUTCOMES = [
  "completed",
  "partial",
  "exploratory",
  "stuck",
  "abandoned",
] as const;
export type Outcome = (typeof OUTCOMES)[number];

export const ORIGINS = ["all", "adobe", "non-adobe"] as const;
export type Origin = (typeof ORIGINS)[number];

export interface OutcomePoint {
  date: string;
  counts: Partial<Record<Outcome, number>>;
}

export interface OutcomeSeriesResponse {
  granularity: "day";
  from: string;
  to: string;
  outcomes: typeof OUTCOMES;
  points: OutcomePoint[];
}

export interface TopUser {
  userId: string;
  totalTokens: number;
  totalCostUsd: number;
  sessionCount: number;
}

export interface TopUsersResponse {
  from: string;
  to: string;
  users: TopUser[];
}

export const PRIMARY_TASKS = [
  "page_migration",
  "refinement",
  "skill_authoring",
  "content_generation",
  "other",
] as const;
export type PrimaryTask = (typeof PRIMARY_TASKS)[number];

export interface TaskBreakdownEntry {
  primaryTask: PrimaryTask;
  sessions: number;
}

export interface TaskBreakdownResponse {
  from: string;
  to: string;
  tasks: TaskBreakdownEntry[];
  unclassifiedSessions: number;
}

export interface SessionSummary {
  sessionId: string;
  userId: string | null;
  startedAt: string;
  endedAt: string;
  durationMin: number;
  eventCount: number;
  primaryTask: PrimaryTask | null;
  outcome: Outcome | null;
  sessionFeel: string | null;
  engagementDepth: string | null;
  journeyHeadline: string | null;
  safetyFlags: string[];
  harness: string | null;
}

export interface SessionsListResponse {
  from: string;
  to: string;
  total: number;
  limit: number;
  offset: number;
  sessions: SessionSummary[];
}

export interface JourneyMilestone {
  at_min: number;
  event: string;
}

export interface SessionDetail extends SessionSummary {
  journeySummary: string[];
  journeyMilestones: JourneyMilestone[];
  rationales: Record<string, string>;
  classifierVersion: string | null;
  classifiedAt: string | null;
  modelsUsed: string[];
  sliccVersionsUsed: string[];
  harnessesUsed: string[];
}

export interface SessionDetailResponse {
  session: SessionDetail;
}

export const SESSION_SORT_FIELDS = [
  "started_at",
  "user_id",
  "duration_min",
  "event_count",
  "primary_task",
  "outcome",
  "session_feel",
] as const;

export type SessionSortField = (typeof SESSION_SORT_FIELDS)[number];
export type SessionSortDir = "asc" | "desc";

export const SESSION_FEELS = [
  "smooth",
  "productive",
  "friction",
  "wandering",
  "blocked",
  "confused",
] as const;
export type SessionFeel = (typeof SESSION_FEELS)[number];

export interface SessionFilters {
  tasks: PrimaryTask[];
  outcomes: Outcome[];
  feels: SessionFeel[];
  startedAfter: string | null;
  startedBefore: string | null;
  durationMinMin: number | null;
  durationMinMax: number | null;
  eventCountMin: number | null;
  eventCountMax: number | null;
  /** Case-insensitive substring match against `user_id`; null skips. */
  userIdQuery: string | null;
  /** Harness allow-list; empty = no filter. */
  harnesses: string[];
}

export const EMPTY_SESSION_FILTERS: SessionFilters = {
  tasks: [],
  outcomes: [],
  feels: [],
  startedAfter: null,
  startedBefore: null,
  durationMinMin: null,
  durationMinMax: null,
  eventCountMin: null,
  eventCountMax: null,
  userIdQuery: null,
  harnesses: [],
};

export function hasActiveSessionFilters(f: SessionFilters): boolean {
  return (
    f.tasks.length > 0 ||
    f.outcomes.length > 0 ||
    f.feels.length > 0 ||
    f.startedAfter !== null ||
    f.startedBefore !== null ||
    f.durationMinMin !== null ||
    f.durationMinMax !== null ||
    f.eventCountMin !== null ||
    f.eventCountMax !== null ||
    (f.userIdQuery !== null && f.userIdQuery !== "") ||
    f.harnesses.length > 0
  );
}

export function countActiveSessionFilters(f: SessionFilters): number {
  let n = f.tasks.length + f.outcomes.length + f.feels.length;
  if (f.startedAfter !== null) n++;
  if (f.startedBefore !== null) n++;
  if (f.durationMinMin !== null) n++;
  if (f.durationMinMax !== null) n++;
  if (f.eventCountMin !== null) n++;
  if (f.eventCountMax !== null) n++;
  if (f.userIdQuery !== null && f.userIdQuery !== "") n++;
  n += f.harnesses.length;
  return n;
}

export interface TopSkill {
  skill: string;
  calls: number;
  sessions: number;
  users: number;
}

export interface TopSkillsResponse {
  from: string;
  to: string;
  skills: TopSkill[];
}

export interface TopSprinkle {
  channel: string;
  events: number;
  sessions: number;
  users: number;
}

export interface TopSprinklesResponse {
  from: string;
  to: string;
  sprinkles: TopSprinkle[];
}

export interface ModelDistributionRow {
  model: string;
  events: number;
  tokens: number;
  users: number;
}

export interface ModelDistributionResponse {
  from: string;
  to: string;
  models: ModelDistributionRow[];
}

export const HARNESSES = ['slicc', 'claude-code', 'copilot', 'pi', 'other'] as const;
export type Harness = (typeof HARNESSES)[number];

export interface HarnessDistributionRow {
  harness: string;
  events: number;
  users: number;
}

export interface HarnessDistributionResponse {
  from: string;
  to: string;
  harnesses: HarnessDistributionRow[];
}

export interface PricingResponse {
  models: Record<
    string,
    {
      inputPerMTok: number;
      outputPerMTok: number;
      cacheWrite5mPerMTok: number;
      cacheWrite1hPerMTok: number;
      cacheReadPerMTok: number;
    }
  >;
  notes: string[];
  lastVerified: string;
}

// ── Users tab ──────────────────────────────────────────────────────────────

export type UserOrigin = "adobe" | "non-adobe" | "unknown";

export interface UserSummary {
  userId: string;
  origin: UserOrigin;
  sessions: number;
  totalTokens: number;
  totalCostUsd: number;
  /** null when the user has zero classified sessions in the window */
  completionRate: number | null;
  classifiedSessions: number;
  completedSessions: number;
  models: string[];
  firstSeen: string;
  lastSeen: string;
}

export const USER_SORT_FIELDS = [
  "sessions",
  "total_tokens",
  "total_cost",
  "completion_rate",
  "last_seen",
  "user_id",
] as const;
export type UserSortField = (typeof USER_SORT_FIELDS)[number];
export type UserSortDir = "asc" | "desc";

export interface UserFilters {
  minSessions: number | null;
  minCompletionRate: number | null;
  tasks: PrimaryTask[];
}

export const EMPTY_USER_FILTERS: UserFilters = {
  minSessions: null,
  minCompletionRate: null,
  tasks: [],
};

export function hasActiveUserFilters(f: UserFilters): boolean {
  return (
    f.minSessions !== null || f.minCompletionRate !== null || f.tasks.length > 0
  );
}

export function countActiveUserFilters(f: UserFilters): number {
  let n = f.tasks.length;
  if (f.minSessions !== null) n++;
  if (f.minCompletionRate !== null) n++;
  return n;
}

export interface UsersListResponse {
  from: string;
  to: string;
  total: number;
  limit: number;
  offset: number;
  users: UserSummary[];
}

export interface UserTokenSeriesPoint {
  date: string;
  totalTokens: number;
  costUsd: number;
}

export interface UserDetail extends UserSummary {
  tokenSeries: UserTokenSeriesPoint[];
  outcomes: Partial<Record<Outcome, number>>;
  unclassifiedSessions: number;
  topSkills: TopSkill[];
  topSprinkles: TopSprinkle[];
  recentSessions: SessionSummary[];
}

export interface UserDetailResponse {
  user: UserDetail;
}

export const USER_SESSIONS_BUCKETS = ["1", "2-4", "5-20", "21+"] as const;
export type UserSessionsBucket = (typeof USER_SESSIONS_BUCKETS)[number];

export const USER_COMPLETION_BUCKETS = [
  "0%",
  "1-25%",
  "26-50%",
  "51-75%",
  "76-100%",
  "no_data",
] as const;
export type UserCompletionBucket = (typeof USER_COMPLETION_BUCKETS)[number];

export interface UserSessionsHistogramEntry {
  bucket: UserSessionsBucket;
  users: number;
}

export interface UserCompletionHistogramEntry {
  bucket: UserCompletionBucket;
  users: number;
}

export interface NewVsReturningPoint {
  date: string;
  new: number;
  returning: number;
}

export interface UsersDistributionResponse {
  from: string;
  to: string;
  granularity: Granularity;
  kpis: {
    activeUsers: number;
    adobe: number;
    nonAdobe: number;
  };
  sessionsHistogram: UserSessionsHistogramEntry[];
  completionHistogram: UserCompletionHistogramEntry[];
  newVsReturning: NewVsReturningPoint[];
}

// ── Spend quota ────────────────────────────────────────────────────

export interface QuotaInfo {
  spentDollars: number;
  budgetDollars: number;
  resetsAt: string;
  allowed: boolean;
}

export interface BulkQuotaResponse {
  quotas: Record<string, QuotaInfo>;
}

export type UsageWindow = "7d" | "14d" | "30d";

/** Live quota state for the rolling-7d view (sourced from SpendQuotaDOs). */
export interface UsageUserQuota {
  userId: string;
  origin: "adobe" | "non-adobe";
  spentDollars: number;
  budgetDollars: number;
  resetsAt: string;
  allowed: boolean;
  pctUsed: number;
}

/** Historical spend for 14d/30d views (computed from D1 token events). */
export interface UsageUserHistorical {
  userId: string;
  origin: "adobe" | "non-adobe";
  spentDollars: number;
}

export type UsageUser = UsageUserQuota | UsageUserHistorical;

export interface UsageResponse {
  window: UsageWindow;
  users: UsageUser[];
  isAdmin: boolean;
}
